/**
 * Default DataGrid columns and row action buttons for SeparationWorklistPage.
 */
import { Box, Button, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useHistory } from 'react-router-dom';
import { RowActionConfig, SeparationWorklistConfig, WorklistRow } from './types';

type RouterHistory = ReturnType<typeof useHistory>;

/** Renders config.rowActions as buttons; prefers onAction over getPath when both exist. */
const renderRowActions = (row: WorklistRow, actions: RowActionConfig[], history: RouterHistory) => (
  <Stack direction='row' spacing={2}>
    {actions.map(action => {
      const hasHandler = Boolean(action.onAction || action.getPath);

      return (
        <Box key={action.label}>
          <Button
            variant={action.getVariant?.(row) ?? 'outlined'}
            disabled={(action.isDisabled?.(row) ?? false) || !hasHandler}
            sx={{ textTransform: 'none' }}
            onClick={async () => {
              if (action.onAction) {
                await action.onAction(row);
                return;
              }
              if (action.getPath) {
                history.push(action.getPath(row));
              }
            }}>
            <Typography>{action.label}</Typography>
          </Button>
        </Box>
      );
    })}
  </Stack>
);

/** Standard worklist columns; override via config.columns if a source needs extras. */
export const buildDefaultColumns = (
  rowActions: RowActionConfig[],
  history: RouterHistory
): GridColDef[] => [
  {
    field: 'registration_number',
    headerName: 'Registration Number',
    width: 180,
    headerAlign: 'center',
    align: 'center'
  },
  {
    field: 'process_number',
    headerName: 'Process Number',
    width: 160,
    headerAlign: 'center',
    align: 'center'
  },
  {
    field: 'submission_id',
    headerName: 'Submission ID',
    width: 160,
    headerAlign: 'center',
    align: 'center'
  },
  {
    field: 'trade_name',
    headerName: 'Trade Name',
    width: 220,
    headerAlign: 'center',
    align: 'left',
    renderCell: params => <Typography>{params?.row?.drug_data?.trade_name ?? ''}</Typography>
  },
  {
    field: 'generic_name',
    headerName: 'Generic Name',
    width: 220,
    headerAlign: 'center',
    align: 'left',
    renderCell: params => <Typography>{params?.row?.drug_data?.generic_name ?? ''}</Typography>
  },
  {
    field: 'filename',
    headerName: 'File Name',
    width: 320,
    headerAlign: 'center',
    align: 'left'
  },
  {
    field: 'qc_status',
    headerName: 'QC Status',
    width: 140,
    headerAlign: 'center',
    align: 'center'
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: Math.max(280, rowActions.length * 220),
    headerAlign: 'left',
    align: 'left',
    sortable: false,
    filterable: false,
    renderCell: params => renderRowActions(params.row, rowActions, history)
  }
];

export const resolveColumns = (
  config: SeparationWorklistConfig,
  history: RouterHistory
): GridColDef[] => config.columns ?? buildDefaultColumns(config.rowActions, history);
